import React, { useEffect, useState } from 'react';
import { Facebook } from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const extractError = (e: any) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Erro inesperado';

const MetaProviderConfigForm: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [appId, setAppId] = useState('');
  const [configId, setConfigId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [webhookVerifyToken, setWebhookVerifyToken] = useState('');
  const [appSecretConfigured, setAppSecretConfigured] = useState(false);
  const [webhookVerifyTokenConfigured, setWebhookVerifyTokenConfigured] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/superadmin/meta-provider-config');
      setAppId(data.config.appId || '');
      setConfigId(data.config.configId || '');
      setAppSecretConfigured(!!data.config.appSecretConfigured);
      setWebhookVerifyTokenConfigured(!!data.config.webhookVerifyTokenConfigured);
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSalvar = async () => {
    if (!appId || !configId) {
      toast({ title: 'Atenção', description: 'App ID e Config ID são obrigatórios.', variant: 'destructive' });
      return;
    }

    setSalvando(true);
    try {
      const { data } = await api.put('/superadmin/meta-provider-config', { appId, configId, appSecret, webhookVerifyToken });
      setAppSecretConfigured(!!data.config.appSecretConfigured);
      setWebhookVerifyTokenConfigured(!!data.config.webhookVerifyTokenConfigured);
      setAppSecret('');
      setWebhookVerifyToken('');
      toast({ title: 'Sucesso', description: 'Configuração salva com sucesso.' });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Carregando...</div>;
  }

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Facebook className="h-5 w-5 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold dark:text-white">Facebook / Meta</h1>
        </div>
        <Button onClick={handleSalvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-6">
        Credenciais do App Facebook "Tech Provider" usado pelo cadastro automático (Embedded Signup) de canais
        WhatsApp. Únicas para toda a plataforma — não são por empresa.
      </p>

      <Card className="mb-6 p-4 dark:bg-slate-800">
        <div className="grid gap-4 max-w-xl">
          <div>
            <Label htmlFor="metaAppId" className="dark:text-white">App ID</Label>
            <Input
              id="metaAppId"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="Ex: 920988707719446"
              className="mt-1 dark:bg-slate-700 dark:text-white"
            />
          </div>

          <div>
            <Label htmlFor="metaConfigId" className="dark:text-white">Config ID (Embedded Signup)</Label>
            <Input
              id="metaConfigId"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              placeholder="Ex: 1064646413222382"
              className="mt-1 dark:bg-slate-700 dark:text-white"
            />
          </div>

          <div>
            <Label htmlFor="metaAppSecret" className="dark:text-white">App Secret</Label>
            <Input
              id="metaAppSecret"
              type="password"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder={appSecretConfigured ? '•••••••• (configurado — deixe em branco para manter)' : 'Cole o App Secret'}
              className="mt-1 dark:bg-slate-700 dark:text-white"
            />
          </div>

          <div>
            <Label htmlFor="metaWebhookToken" className="dark:text-white">Webhook Verify Token</Label>
            <Input
              id="metaWebhookToken"
              type="password"
              value={webhookVerifyToken}
              onChange={(e) => setWebhookVerifyToken(e.target.value)}
              placeholder={webhookVerifyTokenConfigured ? '•••••••• (configurado — deixe em branco para manter)' : 'Token usado na verificação do webhook da Meta'}
              className="mt-1 dark:bg-slate-700 dark:text-white"
            />
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            developers.facebook.com → Seu App → Configurações → Básico (App ID / App Secret) e WhatsApp → Embedded Signup (Config ID).
          </p>
        </div>
      </Card>
    </div>
  );
};

export default MetaProviderConfigForm;
